import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { persistCache, LocalForageWrapper } from 'apollo3-cache-persist'
import localforage from 'localforage'
import { TOKEN_KEY } from '@/config/local-storage'
import { cache } from '@/lib/graphql/cache'
import {
  isCompanyFrozenError,
  notifyCompanyFrozen,
} from '@/lib/graphql/company-frozen'
import LiveLink from '@/lib/graphql/live-link'

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'none',
  },
  mutate: { fetchPolicy: 'no-cache' },
} as const

export async function createApolloClient({
  cacheKey = 'apollo-cache-persist',
}:
  | {
      cacheKey?: string
    }
  | undefined = {}) {
  const customFetch = (
    uri: string | URL | RequestInfo,
    options?: RequestInit
  ) => {
    const { operationName } = JSON.parse(options?.body as string)
    return fetch(`${uri}?opname=${operationName}`, {
      ...options,
      referrerPolicy: 'no-referrer-when-downgrade',
    })
  }

  const httpLink = createHttpLink({
    uri: import.meta.env.VITE_GRAPHQL_SERVER_URI,
    fetch: customFetch,
  })

  const errorLink = onError(({ graphQLErrors }) => {
    if (
      graphQLErrors?.find((error) => error.message.includes('invalid token'))
    ) {
      localStorage.removeItem(TOKEN_KEY)
      window.location.reload()
    }

    if (graphQLErrors?.some(isCompanyFrozenError)) {
      notifyCompanyFrozen()
    }
  })

  const authLink = setContext((_, { headers }) => {
    const token = localStorage.getItem(TOKEN_KEY)

    return {
      headers: {
        ...headers,
        ['x-token']: token ?? '',
      },
    }
  })

  const liveLink = new LiveLink()

  const link = ApolloLink.from([authLink, liveLink, errorLink, httpLink])

  try {
    await persistCache({
      cache,
      key: cacheKey,
      storage: new LocalForageWrapper(localforage),
      maxSize: 200 * 1024 * 1024, // 200 MB
    })
  } catch (error) {
    console.error('Error restoring Apollo cache', error)
  }

  return new ApolloClient({
    link,
    cache,
    defaultOptions,
    devtools: { enabled: true },
    clientAwareness: { name: 'sevalla-client' },
  })
}
